import { useTranslation } from "react-i18next";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { useCategories } from "@/query/useCategories";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Menu, 
  FolderOpen, 
  Eye, 
  CheckCircle, 
  Plus, 
  QrCode, 
  User,
  Activity,
  TrendingUp
} from "lucide-react";
import { useNavigate } from "react-router";
import { useMemo } from "react";
import { useMenuItemsByRestaurant } from "@/query/useMenuItems";

export default function ManagerDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();  
  const { restaurant, restaurantId } = useCurrentRestaurant();
  const { data: categories, isLoading: categoriesLoading } = useCategories(restaurantId);
  const { data: menuItems, isLoading: menuItemsLoading } = useMenuItemsByRestaurant(restaurantId);

  // Calculate statistics
  const totalCategories = categories?.length || 0;


  // Get active menu items (assuming isAvailable = true means active)
  const activeMenuItems = useMemo(() => {
    if(menuItemsLoading) return 0;
    return !menuItems ? 0 : menuItems.filter(item => item.isAvailable).length;
  }, [menuItems, menuItemsLoading]);

  const totalMenuItems = useMemo(() => {
    if(menuItemsLoading) return 0;
    return !menuItems ? 0 : menuItems.length;
  }, [menuItems, menuItemsLoading]);

  // QR code views - would come from analytics in real implementation
  const qrCodeViews = null; // No data available yet

  // Recent activities - would come from activity logs in real implementation
  const recentActivities: { id: number; action: string; item: string; time: string }[] = [];

  const quickActions = [
    {
      title: t("dashboard.quickActions.addMenuItem.title", "Add Menu Item"),
      description: t("dashboard.quickActions.addMenuItem.description", "Create a new menu item"),
      icon: Plus,
      action: () => navigate("/manager/menu"),
      color: "bg-primary text-primary-foreground hover:bg-primary/90"
    },
    {
      title: t("dashboard.quickActions.manageCategories.title", "Manage Categories"), 
      description: t("dashboard.quickActions.manageCategories.description", "Organize your menu"),
      icon: FolderOpen,
      action: () => navigate("/manager/categories"),
      color: "bg-secondary text-secondary-foreground hover:bg-secondary/90"
    },
    {
      title: t("dashboard.quickActions.generateQRCode.title", "Generate QR Code"),
      description: t("dashboard.quickActions.generateQRCode.description", "Create QR codes for tables"),
      icon: QrCode,
      action: () => navigate("/manager/qr-codes"),
      color: "bg-accent text-accent-foreground hover:bg-accent/90"
    },
    {
      title: t("dashboard.quickActions.updateProfile.title", "Update Profile"),
      description: t("dashboard.quickActions.updateProfile.description", "Manage restaurant info"),
      icon: User,
      action: () => navigate("/manager/profile"),
      color: "bg-muted text-muted-foreground hover:bg-muted/80"
    }
  ];

  if (categoriesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.title", "Dashboard")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.subtitle", "Overview of your restaurant")} - {restaurant?.restaurantName}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {restaurant?.restaurantLogo && (
            <img
              src={restaurant.restaurantLogo}
              alt="Restaurant Logo"
              className="h-12 w-12 object-cover rounded-full"
            />
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("dashboard.stats.totalMenuItems", "Total Menu Items")}
            </CardTitle>
            <Menu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMenuItems}</div>
            <p className="text-xs text-muted-foreground">
              {t("dashboard.stats.acrossCategories", "Across all categories")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("dashboard.stats.activeMenuItems", "Active Menu Items")}
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeMenuItems}</div>
            <p className="text-xs text-muted-foreground">
              {t("dashboard.stats.availableNow", "Available now")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("dashboard.stats.totalCategories", "Total Categories")}
            </CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCategories}</div>
            <p className="text-xs text-muted-foreground">
              {t("dashboard.stats.menuSections", "Menu sections")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("dashboard.stats.qrCodeViews", "QR Code Views")}
            </CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{qrCodeViews ?? 'N/A'}</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline h-3 w-3 mr-1" />
              {t("dashboard.stats.thisMonth", "This month")}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t("dashboard.quickActions.title", "Quick Actions")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className={`h-auto p-4 flex flex-col items-center gap-2 text-center ${action.color} border-none`}
                  onClick={action.action}
                >
                  <action.icon className="h-6 w-6" />
                  <div>
                    <div className="font-semibold text-sm">{action.title}</div>
                    <div className="text-xs opacity-90">{action.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t("dashboard.recentActivities.title", "Recent Activities")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="h-2 w-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.action}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {activity.item}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t("dashboard.recentActivities.noActivities", "No recent activities available")}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Profile Access */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t("dashboard.profile.title", "Restaurant Profile")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {restaurant?.restaurantLogo && (
                <img
                  src={restaurant.restaurantLogo}
                  alt="Restaurant Logo"
                  className="h-16 w-16 object-cover rounded-full"
                />
              )}
              <div>
                <h3 className="font-semibold text-lg">{restaurant?.restaurantName}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {restaurant?.description || t("dashboard.profile.noDescription", "No description available")}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  {restaurant?.businessType} • {restaurant?.phone}
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/manager/profile")} variant="outline">
              {t("dashboard.profile.edit", "Edit Profile")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}